'use client';
import { gsap } from 'gsap';
import { useRef, useEffect } from 'react';
import noop from 'lodash/noop';
import { useBreadcrumbs } from '@repo/ui/src/context/BreadCrumbProvider';
import { useIsMobile, useMediaQuery } from '@repo/ui/src/utils/useMediaQuery';
import Section from '@repo/ui/src/components/Section';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { imageFullWidthXs, imageFullWidthSmAndUp } from '@style/variables';
import PauseIcon from '@repo/ui/src/components/Icons/PauseIcon';
import PlayIcon from '@repo/ui/src/components/Icons/PlayIcon';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { ImageProps } from '@repo/ui/src/types/image';
import { TitledSplitToutContent } from '@components/ToutContent';
import useWindowSize from '../shared/window-size';
import VideoRendered from '@repo/ui/src/components/BackgroundVideo';

interface Props {
  toutContent: ToutContentProps;
  playButtonLabel: string;
  pauseButtonLabel: string;
  backgroundImage: ImageProps;
  bottomContentSection: ToutContentProps;
}

const VideoFadeHero = ({
  toutContent,
  playButtonLabel,
  pauseButtonLabel,
  backgroundImage,
  bottomContentSection,
}: Props) => {
  const isMobile = useIsMobile();
  const [width] = useWindowSize();

  const { setDarkBackground } = useBreadcrumbs();

  const IMAGE_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/image-link';
  const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';
  const FULL_TO_THEATER = 1920;

  let isVideo;
  let vidWidth;
  const reduceMotion = useMediaQuery('(prefers-reduced-motion)');

  if (backgroundImage && backgroundImage._meta.schema === IMAGE_META_SCHEMA) {
    isVideo = false;
  } else if (backgroundImage && backgroundImage._meta.schema === VIDEO_META_SCHEMA) {
    isVideo = true;
    vidWidth = width > FULL_TO_THEATER ? FULL_TO_THEATER : width;
    if (!vidWidth) {
      // Won't have width on server side, so use ssr media query to get placeholder image width
      vidWidth = isMobile ? imageFullWidthXs : imageFullWidthSmAndUp;
    }
  }
  const ref = useRef<any>(null);

  const togglePlayVideo = () => {
    const element = ref.current;
    if (element) {
      const videoElement = element.querySelector('video');
      const playIcon = element.querySelector('.play-icon');
      const pauseIcon = element.querySelector('.pause-icon');
      const iconButton = element.querySelector('.icon-button');

      if (videoElement) {
        if (videoElement.autoplay) {
          videoElement.autoplay = false;
          videoElement.pause();
          pauseIcon.style.display = 'none';
          playIcon.style.display = 'block';
          iconButton.ariaLabel = playButtonLabel || 'Play Video';
        } else {
          videoElement.autoplay = true;
          videoElement.play().catch(noop);
          playIcon.style.display = 'none';
          pauseIcon.style.display = 'block';
          iconButton.ariaLabel = pauseButtonLabel || 'Pause Video';
        }
      }
    }
  };

  useEffect(() => {
    const element = ref.current;
    if (element) {
      const hero = element.querySelector('.video-fade-hero');
      const videoBanner = element.querySelector('.video-banner');
      const fadeLayer = element.querySelector('.fade-layer');
      // @ts-expect-error gsap
      const videoHeroTimeline = new gsap.timeline({
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: '75% center',
          scrub: 0.5,

          onToggle: (self) => {
            const videoElement = element.querySelector('video');
            // pausing the video so it doesn't continue to play in the background once this element is not triggering anymore
            if (videoElement) {
              if (self.progress >= 1 && !self.isActive) {
                videoElement.pause();
              } else if (videoElement.autoplay !== false) {
                videoElement.play().catch(noop);
              }
            }
          },
        },
      });

      videoHeroTimeline.to(videoBanner, {
        opacity: 0,
      });
      videoHeroTimeline.to(fadeLayer, {
        opacity: 0,
      });
    }
  }, []);

  useEffect(() => {
    if (backgroundImage) {
      setDarkBackground(true);
    }
    return () => setDarkBackground(false);
  }, [backgroundImage, setDarkBackground]);

  return (
    <section ref={ref}>
      <Section dark className="video-fade-hero !tw-bg-transparent">
        <div
          className="fade-layer tw-w-full tw-h-screen tw-fixed tw-top-0 tw-left-0 tw-z-[99] tw-bg-neutral-600"
          style={{ opacity: 1 }}
        />
        <div
          className="video-banner tw-w-full tw-h-screen tw-fixed tw-top-0 tw-left-0 tw-z-[99]"
          style={{ opacity: 0.7 }}
        >
          {isVideo && vidWidth && (
            <VideoRendered
              video={backgroundImage}
              width={vidWidth}
              fillContainer
              className="video-player tw-w-full tw-h-full tw-block tw-absolute tw-object-cover tw-z-[99]"
            />
          )}
        </div>
        <div className="tw-relative tw-z-101 tw-pt-[2rem]">
          <ToutContent
            dark
            divider
            flexible
            {...toutContent}
            headingStyleVariant="h1"
            className="tw-relative tw-flex tw-justify-center tw-items-center tw-text-center tw-z-101 tw-min-h-[85vh] tw-px-6"
          />
          {!reduceMotion && (
            <button
              className="icon-button tw-ml-5 tw-rounded-full focus-visible:tw-outline-2 focus-visible:tw-outline-brand-300"
              aria-label={pauseButtonLabel || 'Pause Video'}
              onClick={() => togglePlayVideo()}
            >
              <div className="pause-icon tw-pl-2">
                <PauseIcon color="white" />
              </div>
              <div className="play-icon tw-pl-2" style={{ display: 'none' }}>
                <PlayIcon color="white" />
              </div>
            </button>
          )}
          {/* @ts-expect-error missing props */}
          <TitledSplitToutContent
            {...bottomContentSection}
            className="tw-relative tw-flex tw-justify-center tw-items-center tw-text-center tw-z-101 tw-min-h-[85vh] tw-px-6"
          />
        </div>
      </Section>
    </section>
  );
};

export default VideoFadeHero;
